
/**
 * Basic object for models that uses emitter to emit change events
 */
const springyModel = {
    /**
     * These are also the properties that go in the object output by toJSON
     * So it's good for the data you might want to output and send via ajax for saving.
     * Note: Default values should not be complicated arrays or objects. That just causes trouble
     * if you are creating multiple objects from the parent object.
     * Don't include objects with prototypes or functions in defaults.
     * @prop {Object} defaults Default property values we can fallback to.
     */
    defaults: {},
    /**
     * Assign an instance SuiEventEmitter here and you can trigger events when the model changes.
     * @prop {SuiEventEmitter} emitter Event emitter object
     */
    emitter: null,
    /**
     * Initialize a new object
     * This will not set properties with silent option set to true (i.e. no events are triggered)
     * For linked object, use an "initialize" method and call this.init() in it rather than overriding init.
     * @param {Object} config Property/values to set.
     * @param {Object} emitter eventEmitter object.
     */
    init: function(config, emitter) {
        this.set(config, { silent: true });
        if (typeof emitter !== 'undefined') {
            this.emitter = emitter;
        }
    },
    /**
     * Get a property or fallback to defaults
     * @param {String} prop Property to retrieve
     * @return {Object|String|Number|Array|Boolean|null}
     */
    get: function(prop) {
        if (typeof this[prop] !== 'undefined') {
            return this[prop];
        }
        const value = (typeof this.defaults[prop] !== 'undefined') ? this.defaults[prop] : null;
        // protect against passing up an array or object by reference
        // and thus changing the parent's default instead of the property on the child object
        if (typeof value !== 'object') {
            return value;
        }
        if (value === null) {
            return null;
        }
        // Return a shallow clone.
        return JSON.parse( JSON.stringify( value ) );
    },
    /**
     * Set a property or properties.
     * If first arg is an Object then the second arg becomes the "options" arg and the third arg is ignored.
     * Change events should not be triggered if prop doesn't... change (based on a JSON representation of objects).
     * Note: Object properties can be set differently even if the JSON representation is the same,
     * but no change event will be triggered in that case.
     * Note: If you use "get" to get an object(or array) that was not the default, change it, and then use set, you will not trigger a change event
     * Because get returns a reference to the object, thus you dont even need to use "set"
     * @param {String|Object} prop Property to set, or an object with {property: value}
     * @param {String|Array|Object|Boolean|Number} val value to set
     * @param {Object} options Options, just: silent {Boolean} right now.
     */
    set: function(prop, val, options) {
        const changes = [];
        if (typeof prop === 'object') {
            options = val;
            const props = Object.keys(prop);
            props.forEach(function(p) {
                //if (this.get(p) !== prop[p]) {
                if (!this.sameValues(this.get(p), prop[p])) {
                    changes.push(p);
                }
                this[p] = prop[p];
            }, this);
        } else {
            //if (this.get(prop) !== val) {
            if (!this.sameValues(this.get(prop), val)) {
                changes.push(prop);
            }
            this[prop] = val;
        }
        // defaults to false
        if (typeof options === 'undefined') {
            options = {
                silent: false
            };
        }
        if (options.silent || this.emitter === null) {
            return;
        }
        if (!options.silent && changes.length > 0) {
            changes.forEach(function(field) {
                this.emitter.trigger(`change:${field}`, { model: this });
            }, this);
            // emit generic change
            this.emitter.trigger('change', { model: this });
        }
    },
    /**
     * Compare two properties to see if they are different
     * For objects/arrays we need to account for them being different objects
     * that have the same properties and values.
     * @param {String|Number|Array|Object|Boolean} val1 Existing property value
     * @param {String|Number|Array|Object|Boolean} val2 New property value
     * @return {Boolean}
     */
    sameValues: function(val1, val2) {
        // for efficiency we could do typeof checks
        // and only use JSON for objects...
        return JSON.stringify(val1) === JSON.stringify(val2);
    },
    /**
     * Reset properties to defaults.
     * Note this will not effect properties wihout default values.
     */
    reset: function() {
        const props = Object.keys(this.defaults);
        props.forEach(function(p) {
            // or should this just remove the property
            //this[p] = this.defaults[p];
            delete this[p];
        }, this);
    },
    /**
     * Return data as basic object that is simply JSON stringifiable.
     * This only outputs properties that are in the defaults object.
     * It will get the non-default value, but fallback to the default.
     * Data you don't want output with toJSON should not be in that object.
     * @return {Object}
     */
    toJSON: function() {
        // iterate over defaults, but retrieve non-defaults if found
        const output = {};
        const defaults = Object.keys(this.defaults);
        defaults.forEach(function(prop) {
            output[prop] = (typeof this[prop] !== 'undefined') ? this[prop] : this.defaults[prop];
        }, this);
        return output;
    },
    /**
     * Alias for this.emitter.trigger to trigger an event.
     * Include an event and any arguments after the event that will be passed on to the callback(s).
     */
    trigger: function() {
        // eslint-disable-next-line prefer-spread
        this.emitter.trigger.apply(this.emitter, arguments);
    }
};

export default springyModel;
